import React from 'react';
import { X, FileSpreadsheet, FileText, Download } from 'lucide-react';

interface ExportFormatModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (format: 'excel' | 'csv' | 'pdf') => void;
  title?: string;
  subtitle?: string;
}

const ExportFormatModal: React.FC<ExportFormatModalProps> = ({
  isOpen,
  onClose,
  onExport,
  title = "Export Format",
  subtitle = "Choose the format for your export:"
}) => {
  if (!isOpen) return null;

  const formatOptions = [
    {
      key: 'excel' as const,
      title: 'Excel (.xlsx)',
      description: 'Best for data analysis and calculations',
      icon: (
        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
          <span className="text-green-600 font-bold text-lg">X</span>
        </div>
      ),
      color: 'hover:bg-green-50 hover:border-green-200'
    },
    {
      key: 'csv' as const,
      title: 'CSV (.csv)',
      description: 'Simple text format, compatible with most applications',
      icon: (
        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
          <span className="text-blue-600 font-bold text-sm">CSV</span>
        </div>
      ),
      color: 'hover:bg-blue-50 hover:border-blue-200'
    },
    {
      key: 'pdf' as const,
      title: 'PDF (.pdf)',
      description: 'Professional report format, perfect for sharing',
      icon: (
        <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
          <span className="text-red-600 font-bold text-sm">PDF</span>
        </div>
      ),
      color: 'hover:bg-red-50 hover:border-red-200'
    }
  ];

  const handleFormatSelect = (format: 'excel' | 'csv' | 'pdf') => {
    onExport(format);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Format Options */}
        <div className="p-6 space-y-3">
          {formatOptions.map((option) => (
            <button
              key={option.key}
              onClick={() => handleFormatSelect(option.key)}
              className={`
                w-full p-4 border border-gray-100 dark:border-gray-700 rounded-lg 
                text-left transition-all duration-200 
                hover:bg-gray-500/50 dark:hover:bg-gray-700/50
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                ${option.color}
              `}
            >
              <div className="flex items-center space-x-4">
                {option.icon}
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 text-base">
                    {option.title}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {option.description}
                  </p>
                </div>
                <Download size={18} className="text-gray-400" />
              </div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="border border-gray-100 dark:border-gray-700 shadow-lg mx-4 my-2 py-2 px-4 rounded-lg">
          <button
            onClick={onClose}
            className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 transition-colors font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportFormatModal; 